import { API_BASE_URL, API_HEADERS } from '../config';

export interface PhysicalComponent {
  id?: string;
  _id?: string;
  name: string;
  manufacturer?: string;
  size?: number | string;
  grade?: string;
  type?: string;
  ui?: {
    summary?: Record<string, unknown>;
    stats?: Record<string, unknown>;
    byType?: Record<string, unknown>;
  };
  /** Datos de origen (erkul, etc.); la UI prioriza `ui`. */
  raw?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface PhysicalComponentsResponse {
  tipo: string;
  size?: number | string;
  total: number;
  componentes: PhysicalComponent[];
}

export interface ComponentLayout {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  isDefault?: boolean;
  layout: Record<string, unknown>;
}

export interface ShipSlot {
  tipoComponente: string;
  categoria: string;
  slotIndex: number;
  slotNumber?: number;
  size?: number | string;
  assigned_component?: PhysicalComponent | null;
  slotData?: Record<string, unknown>;
}

const getAuthHeaders = (): Record<string, string> => {
  const token = sessionStorage.getItem('corvus_token');
  // Igual que shipService / useApi: X-API-Key viene de API_HEADERS (clave de app); JWT en Authorization.
  return {
    ...API_HEADERS,
    ...(token ? { Authorization: token } : {}),
  };
};

const normalizeError = async (response: Response): Promise<string> => {
  const text = await response.text();
  if (response.status === 404) {
    return 'Endpoint no disponible en el backend actual (404).';
  }
  try {
    const parsed = JSON.parse(text);
    return parsed.mensaje || parsed.message || `Error ${response.status}`;
  } catch {
    return `Error ${response.status}: ${response.statusText}`;
  }
};

const fetchWithFallback = async (urls: string[], init: RequestInit): Promise<Response> => {
  let lastResponse: Response | null = null;
  for (const url of urls) {
    const response = await fetch(url, init);
    if (response.ok) return response;
    lastResponse = response;
    if (response.status !== 404) return response;
  }
  return lastResponse as Response;
};

export const garageService = {
  async getShipSlots(shipId: number | string): Promise<ShipSlot[]> {
    const urls = [
      `${API_BASE_URL}/flota/nave/${shipId}/slots`,
      `${API_BASE_URL}/flota/naves/${shipId}/slots`,
    ];

    const response = await fetchWithFallback(urls, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await normalizeError(response));
    }

    const data = await response.json();
    if (Array.isArray(data)) return data as ShipSlot[];
    return (data.slots || []) as ShipSlot[];
  },

  async getPhysicalComponents(tipo: string, size?: number | string): Promise<PhysicalComponentsResponse> {
    const params = new URLSearchParams({ tipo });
    if (size !== undefined && size !== null && `${size}` !== '') {
      params.set('size', `${size}`);
    }

    const urls = [
      `${API_BASE_URL}/flota/componentes-fisicos?${params.toString()}`,
      `${API_BASE_URL}/flota/componentes-fisicoses?${params.toString()}`,
    ];

    const response = await fetchWithFallback(urls, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await normalizeError(response));
    }

    return response.json();
  },

  async assignPhysicalComponent(
    shipId: number | string,
    tipoComponente: string,
    categoria: string,
    indice: number,
    componenteFisico: PhysicalComponent
  ): Promise<unknown> {
    const body = JSON.stringify({
      componenteFisico: {
        name: componenteFisico.name,
        manufacturer: componenteFisico.manufacturer,
        size: componenteFisico.size,
        grade: componenteFisico.grade,
        type: componenteFisico.type,
      },
    });

    const candidateUrls = [
      // Orden esperado habitual: tipoComponente/categoria
      `${API_BASE_URL}/flota/nave/${shipId}/componentes/${tipoComponente}/${categoria}/${indice}/asignar`,
      `${API_BASE_URL}/flota/naves/${shipId}/componentes/${tipoComponente}/${categoria}/${indice}/asignar`,
      // Fallback: algunos backends usan categoria/tipoComponente
      `${API_BASE_URL}/flota/nave/${shipId}/componentes/${categoria}/${tipoComponente}/${indice}/asignar`,
      `${API_BASE_URL}/flota/naves/${shipId}/componentes/${categoria}/${tipoComponente}/${indice}/asignar`,
    ];

    let lastResponse: Response | null = null;
    for (const method of ['PATCH', 'PUT']) {
      for (const url of candidateUrls) {
        const response = await fetch(url, {
          method,
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body,
        });

        if (response.ok) {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            return response.json();
          }
          return { success: true };
        }

        lastResponse = response;
        if (response.status !== 404 && response.status !== 405) {
          throw new Error(await normalizeError(response));
        }
      }
    }

    throw new Error(await normalizeError(lastResponse as Response));
  },

  async getShipLayouts(shipId: number | string): Promise<ComponentLayout[]> {
    const urls = [
      `${API_BASE_URL}/flota/nave/${shipId}/component-layouts`,
      `${API_BASE_URL}/flota/naves/${shipId}/component-layouts`,
      `${API_BASE_URL}/flota/nave/${shipId}/layouts`,
    ];

    const response = await fetchWithFallback(urls, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      // Si el backend aún no implementa layouts, devolver vacío sin romper UX.
      if (response.status === 404) {
        return [];
      }
      throw new Error(await normalizeError(response));
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.layouts || [];
  },

  async createShipLayout(shipId: number | string, layout: ComponentLayout): Promise<ComponentLayout> {
    const urls = [
      `${API_BASE_URL}/flota/nave/${shipId}/component-layouts`,
      `${API_BASE_URL}/flota/naves/${shipId}/component-layouts`,
      `${API_BASE_URL}/flota/nave/${shipId}/layouts`,
    ];

    const response = await fetchWithFallback(urls, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(layout),
    });

    if (!response.ok) {
      throw new Error(await normalizeError(response));
    }

    return response.json();
  },

  async updateShipLayout(shipId: number | string, layoutId: string, layout: ComponentLayout): Promise<ComponentLayout> {
    const urls = [
      `${API_BASE_URL}/flota/nave/${shipId}/component-layouts/${layoutId}`,
      `${API_BASE_URL}/flota/naves/${shipId}/component-layouts/${layoutId}`,
      `${API_BASE_URL}/flota/nave/${shipId}/layouts/${layoutId}`,
    ];

    const response = await fetchWithFallback(urls, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(layout),
    });

    if (!response.ok) {
      throw new Error(await normalizeError(response));
    }

    return response.json();
  },

  async deleteShipLayout(shipId: number | string, layoutId: string): Promise<void> {
    const urls = [
      `${API_BASE_URL}/flota/nave/${shipId}/component-layouts/${layoutId}`,
      `${API_BASE_URL}/flota/naves/${shipId}/component-layouts/${layoutId}`,
      `${API_BASE_URL}/flota/nave/${shipId}/layouts/${layoutId}`,
    ];

    const response = await fetchWithFallback(urls, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await normalizeError(response));
    }
  },
};
